import {
  addBreadcrumb as sentryAddBreadcrumb,
  setUser as sentrySetUser,
  setContext as sentrySetContext,
  captureReactException as sentryCaptureReactException,
  getClient,
  getGlobalScope,
  type SeverityLevel,
} from '@sentry/react';
import { getLogger, getDatadogSessionId } from './instance';

export type { SeverityLevel };

// `@sentry/astro` is the only thing that ever calls `Sentry.init()`, so a
// `@sentry/react` call made before that init runs (or from a bundle Astro's
// init never reaches) talks to an unbound client and is discarded — no
// error, no signal, nothing. That is exactly what happened for ~3 months
// with `setUser`/`addBreadcrumb`/`captureReactException`. Every wrapper below
// checks for a bound client first, so a repeat of that failure mode shows up
// in Datadog instead of vanishing silently.
let hasLoggedUnboundClient = false;

function warnIfSentryClientUnbound(wrapperName: string) {
  try {
    // Sentry is switched off on purpose outside production (see `sentry.ts`
    // and the `sentry()` block in `packages/springroll/astro.config.mjs`,
    // both gated on this same flag) — an unbound client there is the
    // expected state, not a fault. Staying quiet here keeps the log
    // meaningful in production, which is the only place it can indicate a
    // real problem.
    if (process.env.PUBLIC_SENTRY_DISABLED === 'true') {
      return;
    }

    if (getClient()) {
      return;
    }

    if (hasLoggedUnboundClient) {
      return;
    }
    hasLoggedUnboundClient = true;

    // Logged directly through the Datadog logger rather than
    // `logUnknownErrorResponse`/`logExceptionOnce` — both of those call
    // `addSentryBreadcrumb` themselves, which would re-enter this guard and
    // recurse.
    getLogger().log(
      'Sentry client is not bound',
      {
        key: 'o11y.sentry.client.unbound',
        wrapper: wrapperName,
      },
      'error'
    );
  } catch {
    // The guard must never be the reason the real Sentry call below doesn't
    // happen.
  }
}

// The reverse of this link already exists: `ErrorBoundary` writes the
// Sentry event id into Datadog logs, so a Datadog record can point back to
// the Sentry event it triggered. This is the forward direction — tagging
// every Sentry event with the Datadog browser session it happened in — and
// it is the *only* join available for logged-out traffic, where there is no
// `user.id` to correlate on.
//
// Read at event-capture time (an event processor) rather than pushed from
// the wrapper functions below: a pushed tag is only as fresh as the last
// wrapper call, and Datadog sessions rotate on their own schedule
// (inactivity timeout, hard maximum lifetime) with no wrapper call to
// trigger a re-push. An uncaught error caught by `@sentry/astro`'s global
// handler — the exact case this tag exists for, since it's the only path
// with no `user.id` to fall back on — never touches these wrappers at all.
// Reading the id inside the processor means every captured event, on every
// path, gets whatever session id is current *right now*.
// Both flags are module-scoped, so they dedupe repeat calls within a bundle
// but deliberately not across them: each webpack bundle carries its own copy
// of this module, so a page loading several will register several processors
// on the one shared global scope. That is harmless rather than overlooked —
// the processors are identical and idempotent, each setting the same tag to
// the same value, so N of them produce exactly what one does. A `globalThis`
// flag would dedupe them but has to be keyed by SDK version to stay correct
// (two SDK versions have two different global scopes, and a shared flag would
// leave the second one untagged) — complexity that buys nothing here. The
// only cost of the duplicates is N-1 redundant session-id reads per event
// and, if tagging ever throws, one failure log per bundle instead of one.
//
// Registration runs BEFORE `Sentry.init()` on the webpack path (see
// `sentry.ts`), which is safe because `getGlobalScope()` is a `globalThis`
// singleton with no dependency on a bound client: capture merges the global
// scope's processors in at event time, not at init time. Pinned in both
// orderings by `sentry-session-tagging-registration-order.spec.ts`.
let hasRegisteredDatadogSessionTagging = false;
let hasLoggedDatadogSessionTagFailure = false;

// Exported so it can be called from both Sentry init sites (see
// `sentry.ts` and `O11y.tsx`) rather than registered as a module-scope side
// effect here: this package sets `"sideEffects": false` in package.json, so
// a bundler is entitled to drop a module that's only ever imported for its
// side effects. An explicit function called from each entry point is the
// only registration path that survives tree-shaking.
export function registerDatadogSessionTagging() {
  if (hasRegisteredDatadogSessionTagging) {
    return;
  }
  hasRegisteredDatadogSessionTagging = true;

  getGlobalScope().addEventProcessor((event) => {
    try {
      const sessionId = getDatadogSessionId();

      if (!sessionId) {
        return event;
      }

      event.tags = { ...event.tags, datadog_session_id: sessionId };
      return event;
    } catch (error) {
      // An event processor that returns `null`/`undefined` DROPS the
      // event entirely. Losing a real error because a debugging tag
      // failed to attach would be far worse than the staleness bug this
      // processor exists to fix, so every path — including this one —
      // must return the event unchanged.
      //
      // Logged directly through the Datadog logger rather than
      // `logUnknownErrorResponse`/`logExceptionOnce` — both of those call
      // `addSentryBreadcrumb` themselves, which would re-enter this event
      // processor and recurse.
      if (!hasLoggedDatadogSessionTagFailure) {
        hasLoggedDatadogSessionTagFailure = true;
        getLogger().log(
          'Failed to tag Sentry event with Datadog session id',
          {
            key: 'o11y.datadog.session.tag.failed',
            error: String(error),
          },
          'error'
        );
      }
      return event;
    }
  });
}

export function addBreadcrumb(
  ...args: Parameters<typeof sentryAddBreadcrumb>
): ReturnType<typeof sentryAddBreadcrumb> {
  warnIfSentryClientUnbound('addBreadcrumb');
  return sentryAddBreadcrumb(...args);
}

export function setUser(
  ...args: Parameters<typeof sentrySetUser>
): ReturnType<typeof sentrySetUser> {
  warnIfSentryClientUnbound('setUser');
  return sentrySetUser(...args);
}

export function setContext(
  ...args: Parameters<typeof sentrySetContext>
): ReturnType<typeof sentrySetContext> {
  warnIfSentryClientUnbound('setContext');
  return sentrySetContext(...args);
}

export function captureReactException(
  ...args: Parameters<typeof sentryCaptureReactException>
): ReturnType<typeof sentryCaptureReactException> {
  warnIfSentryClientUnbound('captureReactException');
  return sentryCaptureReactException(...args);
}
